import React, { useEffect, useState } from "react";
import {
  Modal,
  useTheme,
  mergeStyleSets,
  FontWeights,
  IconButton,
  IButtonStyles,
  IIconProps,
  Stack,
  StackItem,
  IStackTokens,
  TextField,
  DefaultButton,
  List,
  normalize,
  IList,
  Label,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { extractErrorMessage, toErrorMessageBar, toSuccessMessageBar } from "@helpers/alertUtils";
import { ICustomerInfo } from "@models/dto/Rocketta/LicenseServer/Models/Customers/ICustomerInfo";
import { IEditCustomerRequest } from "@models/dto/Rocketta/LicenseServer/Models/Customers/IEditCustomerRequest";
import { CustomersApi } from "@api/customersApi";
import { LicensesApi } from "@api/licensesApi";
import { IShortLicenseInfo } from "@models/dto/Rocketta/LicenseServer/Models/Licenses/IShortLicenseInfo";

export interface ICustomerModalProps {
  customer: ICustomerInfo;
  customerModalVisible: boolean;
  hideCustomerModal: () => void;
  onUpdate: (customer: ICustomerInfo) => void;
}

export const CustomerModal: React.FC<ICustomerModalProps> = (props: ICustomerModalProps) => {
  const { customer, customerModalVisible, hideCustomerModal, onUpdate } = props;

  const [url, setUrl] = useState<string>(customer.url);
  const [licenses, setLicenses] = useState<IShortLicenseInfo[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isUpdatedCustomer, setIsUpdatedCustomer] = useState<boolean>(false);

  const listRef: React.RefObject<IList> = React.useRef(null);

  const titleId = useId("title");
  const cancelIcon: IIconProps = { iconName: "Cancel" };
  const theme = useTheme();
  const iconButtonStyles: Partial<IButtonStyles> = {
    root: {
      color: theme.palette.neutralPrimary,
      marginLeft: "auto",
      marginTop: "4px",
      marginRight: "2px",
    },
    rootHovered: {
      color: theme.palette.neutralDark,
    },
  };
  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      width: "500px",
    },
    header: [
      theme.fonts.xLargePlus,
      {
        flex: "1 1 auto",
        borderTop: `4px solid ${theme.palette.themePrimary}`,
        color: theme.palette.neutralPrimary,
        display: "flex",
        alignItems: "center",
        fontWeight: FontWeights.semibold,
        padding: "12px 12px 14px 24px",
      },
    ],
    heading: {
      color: theme.palette.neutralPrimary,
      fontWeight: FontWeights.semibold,
      fontSize: "inherit",
      margin: "0",
    },
    body: {
      flex: "4 4 auto",
      padding: "0 24px 24px 24px",
      overflowY: "hidden",
      selectors: {
        p: { margin: "14px 0" },
        "p:first-child": { marginTop: 0 },
        "p:last-child": { marginBottom: 0 },
      },
    },
    itemWrapper: {
      display: "flex",
      alignItems: "center",
      marginBottom: "5px",
    },
    itemContent: [
      theme.fonts.medium,
      normalize,
      {
        position: "relative",
        boxSizing: "border-box",
        display: "block",
        borderLeft: "3px solid " + theme.palette.themePrimary,
        paddingLeft: 27,
      },
    ],
  });
  const tokens: IStackTokens = { childrenGap: 10 };

  const numberOfItemsOnPage = 50;
  const itemHeight = 25;
  const getPageHeight = (idx: number): number => {
    let h = 0;
    for (let i = idx; i < idx + numberOfItemsOnPage; ++i) {
      h += itemHeight;
    }
    return h;
  };

  useEffect(() => {
    LicensesApi.getLicensesByCustomer(customer.id)
      .then((result) => {
        const licensesItems = result.data.data;
        setLicenses(licensesItems);
        return licensesItems;
      })
      .catch((err) => {
        const message = extractErrorMessage(err);
        setErrorMessage(message);
      });
  }, [customer]);

  const onRenderCell = React.useCallback(
    (item: IShortLicenseInfo, index: number): JSX.Element => {
      return (
        <div data-is-focusable className={contentStyles.itemWrapper}>
          <div className={contentStyles.itemContent}>
            {item.product.name} - {item.expirationDate} ({item.licenseId} )
          </div>
        </div>
      );
    },
    [contentStyles]
  );

  const onSaveButtonClick = async (): Promise<void> => {
    const editCustomerRequest: IEditCustomerRequest = {
      id: customer.id,
      url: url,
    };
    try {
      const updatedCustomerResponse = await CustomersApi.editCustomer(editCustomerRequest);
      setIsUpdatedCustomer(true);
      onUpdate(updatedCustomerResponse.data.data);
    } catch (err) {
      const msg = extractErrorMessage(err);
      setErrorMessage(msg);
    }
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={customerModalVisible}
      onDismiss={hideCustomerModal}
      isBlocking={true}
      containerClassName={contentStyles.container}
    >
      <div className={contentStyles.header}>
        <h2 className={contentStyles.heading} id={titleId}>
          Customer Info
        </h2>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={hideCustomerModal}
        />
      </div>
      <div className={contentStyles.body}>
        <Stack tokens={tokens}>
          <StackItem>
            <TextField value={url} onChange={(ev, newValue) => setUrl(newValue)} label="Url" />
          </StackItem>
          <StackItem>
            <TextField disabled value={customer.tenantId} label="TenantId" />
          </StackItem>
          <StackItem>
            <Label>Licenses</Label>
          </StackItem>
          <StackItem>
            <List componentRef={listRef} items={licenses} getPageHeight={getPageHeight} onRenderCell={onRenderCell} />
          </StackItem>
          <StackItem>
            <DefaultButton onClick={onSaveButtonClick}>Save</DefaultButton>
          </StackItem>
          {errorMessage && <StackItem>{toErrorMessageBar(errorMessage)}</StackItem>}
          {isUpdatedCustomer && <StackItem>{toSuccessMessageBar("Updated successfully")}</StackItem>}
        </Stack>
      </div>
    </Modal>
  );
};
